import { useTranslation } from "react-i18next";
import { SelectField } from "@/components/reusable";
import {
  HISTORY_FILTER_ALL,
  type HistoryFilterOption,
  type HistoryFilterState,
  type HistorySortOrder,
} from "@/lib/historyFilters";

interface HistoryFilterBarProps {
  filters: HistoryFilterState;
  onChange: (next: HistoryFilterState) => void;
  showProviderFilter: boolean;
  /** Provider options already labeled by name (value = providerId). */
  providerOptions: HistoryFilterOption[];
  schemaOptions: HistoryFilterOption[];
  qualityOptions: HistoryFilterOption[];
}

/**
 * Shared filter controls for global and provider-scoped ingestion history.
 * Provider select is hidden when the page is already scoped to one provider.
 */
export function HistoryFilterBar({
  filters,
  onChange,
  showProviderFilter,
  providerOptions,
  schemaOptions,
  qualityOptions,
}: HistoryFilterBarProps) {
  const { t } = useTranslation();

  /** Map SelectField clear ("") back to the All sentinel. */
  const toFilterValue = (value: string) => (value === "" ? HISTORY_FILTER_ALL : value);

  const providerSelectOptions = [
    { value: HISTORY_FILTER_ALL, label: t("history.allProviders") },
    ...providerOptions,
  ];
  const schemaSelectOptions = [
    { value: HISTORY_FILTER_ALL, label: t("history.filters.allSchemas") },
    ...schemaOptions,
  ];
  const qualitySelectOptions = [
    { value: HISTORY_FILTER_ALL, label: t("history.filters.allQualityStatuses") },
    ...qualityOptions,
  ];
  const sortOptions = [
    { value: "newest", label: t("history.filters.sortNewest") },
    { value: "oldest", label: t("history.filters.sortOldest") },
  ];

  return (
    <div
      className="mb-6 grid gap-4 animate-fade-in-up stagger-2 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="history-filter-bar"
    >
      {showProviderFilter ? (
        <div>
          <label className="text-sm font-semibold mb-2 block">{t("history.selectProvider")}</label>
          <SelectField
            value={filters.providerId}
            onValueChange={(value) => onChange({ ...filters, providerId: toFilterValue(value) })}
            options={providerSelectOptions}
            placeholder={t("history.allProviders")}
            className="max-w-none"
          />
        </div>
      ) : null}

      <div>
        <label className="text-sm font-semibold mb-2 block">{t("history.filters.schema")}</label>
        <SelectField
          value={filters.schemaId}
          onValueChange={(value) => onChange({ ...filters, schemaId: toFilterValue(value) })}
          options={schemaSelectOptions}
          placeholder={t("history.filters.allSchemas")}
          className="max-w-none"
        />
      </div>

      <div>
        <label className="text-sm font-semibold mb-2 block">{t("history.filters.quality")}</label>
        <SelectField
          value={filters.qualityStatus}
          onValueChange={(value) => onChange({ ...filters, qualityStatus: toFilterValue(value) })}
          options={qualitySelectOptions}
          placeholder={t("history.filters.allQualityStatuses")}
          className="max-w-none"
        />
      </div>

      <div>
        <label className="text-sm font-semibold mb-2 block">{t("history.filters.sort")}</label>
        <SelectField
          value={filters.sortOrder}
          onValueChange={(value) =>
            onChange({
              ...filters,
              sortOrder: (value === "oldest" ? "oldest" : "newest") as HistorySortOrder,
            })
          }
          options={sortOptions}
          placeholder={t("history.filters.sortNewest")}
          className="max-w-none"
        />
      </div>
    </div>
  );
}
